import React, { useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { IconButton, MenuItem, Menu } from "@mui/material";
import { Button } from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../helpers/urlHelper";
import TopHeader from "../../components/common/Header/TopHeader";

// Componente Modal
function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  footer = null,
}) {
  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className={`modal-dialog modal-${size} modal-dialog-centered`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

// Modal de confirmación
function ConfirmationModal({ isOpen, onClose, onConfirm, title, message }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm}>
            Confirm
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
}

const TreatmentsList = () => {
  const [treatments, setTreatments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/treatmentManagement`
      );
      console.log("API Response:", response.data); // Para debugging
      setTreatments(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching treatments:", err);
      setError("Failed to load treatments. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (treatment) => {
    navigate("/treatmentTemplateAdd", { state: { treatmentData: treatment } });
  };

  const handleDelete = (treatment) => {
    setSelectedTreatment(treatment);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedTreatment) return;

    try {
      // CORREGIDO: URL sin doble protocolo
      await axios.delete(
        `${API_BASE_URL}/treatmentManagement/${selectedTreatment._id}`
      );
      fetchTreatments();
      alert("Treatment deleted successfully");
    } catch (err) {
      console.error("Error deleting treatment:", err);
      alert(`Error deleting treatment: ${err.message}`);
    } finally {
      setShowDeleteModal(false);
      setSelectedTreatment(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return "bg-secondary";
      case "archived":
        return "bg-warning text-dark";
      case "approved":
        return "bg-success";
      case "canceled":
        return "bg-danger";
      case "under review":
        return "bg-info";
      default:
        return "bg-light text-dark";
    }
  };

  // CORREGIDO: Columnas adaptadas a la estructura real de datos
  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => "",
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = useState(null);
          const open = Boolean(anchorEl);

          const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <>
              <IconButton onClick={handleClick}>
                <span>⋮</span>
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    handleEdit(row.original);
                    handleClose();
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleDelete(row.original);
                    handleClose();
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </>
          );
        },
        size: 50,
      },
      {
        // CORREGIDO: Acceso directo a template.name
        accessorFn: (row) => row.template?.name || "Unnamed",
        id: "name",
        header: "Name",
        Cell: ({ row }) => (
          <div
            className="d-flex align-items-center"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleEdit(row.original)}
          >
            <span>{row.original.template?.name || "Unnamed"}</span>
          </div>
        ),
      },
      {
        // CORREGIDO: Acceso a protocol_code
        accessorFn: (row) => row.template?.protocol_code || "N/A",
        id: "protocol",
        header: "Protocol",
      },
      {
        // CORREGIDO: Acceso a version
        accessorFn: (row) => row.template?.version || "1",
        id: "version",
        header: "Version",
      },
      {
        // CORREGIDO: Acceso a status
        accessorFn: (row) => row.template?.status || "Unknown",
        id: "status",
        header: "Status",
        Cell: ({ row }) => (
          <span
            className={`badge ${getStatusBadgeClass(
              row.original.template?.status
            )}`}
          >
            {row.original.template?.status || "Unknown"}
          </span>
        ),
      },
      {
        // CORREGIDO: Acceso a creation_date
        accessorFn: (row) => row.template?.creation_date,
        id: "creation_date",
        header: "Created",
        Cell: ({ row }) => formatDate(row.original.template?.creation_date),
      },
      {
        // CORREGIDO: Acceso a last_modified
        accessorFn: (row) => row.template?.last_modified,
        id: "last_modified",
        header: "Last Modified",
        Cell: ({ row }) => formatDate(row.original.template?.last_modified),
      },
    ],
    []
  );

  const handleAddNew = () => {
    navigate("/treatmentTemplateAdd");
  };

  if (error) {
    return (
      <div className="container-fluid px-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <main id="app">
            <TopHeader />
    <><div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center my-3">
        <span className="fw-bold">Treatments Templates List</span>
        <Button onClick={handleAddNew}>Add New Treatment</Button>
      </div>

      {/* AGREGADO: Información de debugging */}
      {!isLoading && (
        <div className="mb-3">
          <small className="text-muted">
            Total treatments loaded: {treatments.length}
          </small>
        </div>
      )}

      <MaterialReactTable
        columns={columns}
        data={treatments}
        state={{ isLoading }}
        enableRowSelection={true}
        enableColumnFilters
        enableColumnOrdering
        enableSorting
        enablePagination
        enableGlobalFilter
        positionGlobalFilter="left"
        // AGREGADO: Props adicionales para mejor debugging
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => console.log('Row data:', row.original),
          sx: { cursor: 'pointer' }
        })} />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Treatment"
        message={`Are you sure you want to delete the treatment "${selectedTreatment?.template?.name || "Unnamed"}"? This action cannot be undone.`} />
    </div></>
    </main>
  );
};

export default TreatmentsList;